// v237 admin page for the demo-data seeder (item 58 of
// PRODUCT-ROADMAP.md, item 67 in the recommended delivery order).
// Pure read + reset surface; the actual apply / preview workflow is
// CLI-first via `orbital seed`. The page exists primarily so an
// operator who landed on the install via SSO has a UI handle to
//
//   - confirm whether the install currently has demo data (matches
//     the SeedBanner strip the AppShell renders)
//   - clear the demo data before going live (the "Reset demo data"
//     button — same semantics as `orbital seed --reset`)
//   - preview what each set would create without committing.
//
// Apply is deliberately CLI-only here so an admin doesn't trigger an
// expensive bundle write through a one-click button on the wrong
// install. The CLI's --force flag and the air-gapped check both gate
// the apply path, keeping production deployments safe.

import { useState } from 'react';
import { Sparkles, RefreshCw, Trash2, Eye } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import {
  useSeedStatus,
  useSeedPreview,
  resetSeed,
  type SeedPlan,
} from '../../api/seed';

const SETS: { id: 'minimal' | 'default' | 'full' | 'full-plus' | 'stress'; label: string; hint: string }[] = [
  { id: 'minimal', label: 'minimal', hint: '1 project · 3 repos · 1 SA · 1 block (CI-test-sized)' },
  { id: 'default', label: 'default', hint: '3 projects · ~41 repos (one per format adapter) · 3 SAs · 3 blocks · banners / channels / webhooks / retention / OIDC / Sigstore / pull-gate / backup / CNPG / license-policies' },
  { id: 'full', label: 'full', hint: 'default + extra SAs / blocks / banners / pull-gate policies / signing-key bundles / backup runs' },
  { id: 'full-plus', label: 'full-plus', hint: 'full + doubled banners / signing-keys / backup-runs / webhooks / retention coverage — busier dashboard density for sales demos' },
  { id: 'stress', label: 'stress', hint: 'full-plus + per-project per-format repo fan-out (~120+ repos) — back-end profiling, not a customer-facing demo' },
];

function SeedCurrentState({
  isLoading,
  status,
  resetting,
  onReset,
  onRefresh,
  resetSummary,
  error,
}: Readonly<{
  isLoading: boolean;
  status: ReturnType<typeof useSeedStatus>['data'];
  resetting: boolean;
  onReset: () => void;
  onRefresh: () => void;
  resetSummary: string | null;
  error: string | null;
}>) {
  if (isLoading) {
    return <p className="text-fg-muted">Loading…</p>;
  }
  if (status?.enabled) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Badge tone="success">Active</Badge>
          <span className="text-fg">
            Set <code className="px-1 rounded bg-bg-elevated/80">{status.set_name}</code>
            {' · '}
            {status.item_count} item(s)
          </span>
        </div>
        {status.applied_at && (
          <p className="text-sm text-fg-muted">
            Applied {new Date(status.applied_at).toUTCString()}
            {status.applied_by ? ` by ${status.applied_by}` : ''}
          </p>
        )}
        <div className="flex items-center gap-2 pt-2">
          <Button
            variant="danger"
            size="sm"
            onClick={onReset}
            disabled={resetting}
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            {resetting ? 'Resetting…' : 'Reset demo data'}
          </Button>
          <Button variant="secondary" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Refresh
          </Button>
        </div>
        {resetSummary && <p className="text-sm text-success">{resetSummary}</p>}
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <Badge tone="neutral">Inactive</Badge>
      <p className="text-sm text-fg-muted">
        No demo data on this install. To apply a set, run{' '}
        <code className="px-1 rounded bg-bg-elevated/80">orbital seed</code>{' '}
        (or{' '}
        <code className="px-1 rounded bg-bg-elevated/80">orbital seed --set full-plus</code>)
        from a workstation with admin credentials.
      </p>
      {resetSummary && <p className="text-sm text-success">{resetSummary}</p>}
      {error && <p className="text-sm text-danger">{error}</p>}
    </div>
  );
}

export function AdminSeed() {
  const { data: status, mutate: refresh, isLoading } = useSeedStatus();
  const [previewSet, setPreviewSet] = useState<string | null>(null);
  const { data: plan, isLoading: previewLoading } = useSeedPreview(previewSet);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSummary, setResetSummary] = useState<string | null>(null);

  const handleReset = async () => {
    if (!globalThis.confirm('Reset demo data? This will delete every record `orbital seed` created on this install.')) {
      return;
    }
    setError(null);
    setResetSummary(null);
    setResetting(true);
    try {
      const out = await resetSeed();
      setResetSummary(`Removed ${out.deleted} demo record(s).`);
      await refresh();
    } catch (e) {
      setError(String(e));
    } finally {
      setResetting(false);
    }
  };

  return (
    <AdminShell
      title={
        <span className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-accent" />
          Demo data
        </span>
      }
      description="Seed and reset the eval / sales-demo / tutorial dataset."
    >
      <div className="space-y-6 max-w-4xl">
        <Card>
          <CardHeader title="Current state" description="Read-only — applies happen via the orbital CLI." />
          <CardBody>
            <SeedCurrentState
              isLoading={isLoading}
              status={status}
              resetting={resetting}
              onReset={handleReset}
              onRefresh={() => refresh()}
              resetSummary={resetSummary}
              error={error}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Preview a set"
            description="See what each set would create without committing."
          />
          <CardBody>
            <div className="flex items-center gap-2 flex-wrap">
              {SETS.map((s) => (
                <Button
                  key={s.id}
                  variant={previewSet === s.id ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setPreviewSet(s.id)}
                >
                  <Eye className="h-4 w-4 mr-1.5" />
                  {s.label}
                </Button>
              ))}
            </div>
            {previewSet && (
              <p className="text-xs text-fg-muted mt-2">
                {SETS.find((s) => s.id === previewSet)?.hint}
              </p>
            )}
            <div className="mt-4">
              {previewLoading && <p className="text-fg-muted">Loading plan…</p>}
              {plan && <PlanList plan={plan} />}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="CLI usage" />
          <CardBody>
            <pre className="text-sm text-fg-muted bg-bg-elevated/40 rounded p-3 overflow-x-auto">
{`# Apply the default set (3 projects, ~41 repos, 3 SAs, 3 blocks)
orbital seed

# Sales-demo set — busier dashboard, JSON-friendly for CI
orbital seed --set full-plus --json

# Back-end profiling — per-project per-format repo fan-out
orbital seed --set stress

# Show what would happen, write nothing
orbital seed --dry-run

# Wipe demo data between sales calls
orbital seed --reset

# Override the air-gapped block (use sparingly)
orbital seed --force`}
            </pre>
          </CardBody>
        </Card>
      </div>
    </AdminShell>
  );
}

function PlanList({ plan }: Readonly<{ plan: SeedPlan }>) {
  // Phase A v2 (item 66 / delivery #75) — show only non-zero counts so
  // a `minimal`-set preview stays compact.
  const surfaces: { label: string; count: number }[] = [
    { label: 'Banners', count: plan.banners ?? 0 },
    { label: 'Notification channels', count: plan.notification_channels ?? 0 },
    { label: 'Webhook subscriptions', count: plan.webhook_subscriptions ?? 0 },
    { label: 'Retention policies', count: plan.retention_policies ?? 0 },
    { label: 'Scan rules', count: plan.scan_rules ?? 0 },
    { label: 'OIDC trust policies', count: plan.oidc_trust_policies ?? 0 },
    { label: 'Sigstore trust policies', count: plan.sigstore_trust_policies ?? 0 },
    { label: 'Signing keys (trust bundles)', count: plan.signing_keys ?? 0 },
    { label: 'Pull-gate policies', count: plan.pull_gate_policies ?? 0 },
    { label: 'Quarantine settings', count: plan.quarantine_settings ?? 0 },
    { label: 'Geo-sync peers', count: plan.geosync_peers ?? 0 },
    { label: 'Backup verification runs', count: plan.backup_verification_runs ?? 0 },
    { label: 'CNPG image catalog entries', count: plan.cnpg_catalog_entries ?? 0 },
    { label: 'License policies', count: plan.license_policies ?? 0 },
  ].filter((s) => s.count > 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
      <Section title={`Projects (${plan.projects.length})`} items={plan.projects} />
      <Section title={`Repositories (${plan.repositories.length})`} items={plan.repositories} />
      <Section title={`Service accounts (${plan.service_accounts.length})`} items={plan.service_accounts} />
      <Section title={`Security blocks (${plan.security_blocks.length})`} items={plan.security_blocks} />
      <div className="md:col-span-2 text-fg-muted">
        Audit events: {plan.audit_events}
      </div>
      {surfaces.length > 0 && (
        <div className="md:col-span-2">
          <p className="font-semibold mb-1.5">Customer-facing surfaces</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 text-fg-muted">
            {surfaces.map((s) => (
              <li key={s.label} className="flex justify-between gap-2">
                <span>{s.label}</span>
                <span className="font-mono">{s.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Section({ title, items }: Readonly<{ title: string; items: string[] }>) {
  return (
    <div>
      <p className="font-semibold mb-1.5">{title}</p>
      {items.length === 0 ? (
        <p className="text-fg-muted">(none)</p>
      ) : (
        <ul className="space-y-0.5">
          {items.map((item) => (
            <li key={item} className="font-mono text-fg-muted truncate">
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
